import { R } from 'core/helpers'
import type { Company, CompanyConfigBrandAssets } from 'core/types'

const brandLogoBaseUrl = 'https://storage.googleapis.com/peach-assets-dev'

type CompanyConfigLogos = NonNullable<CompanyConfigBrandAssets['logos']>

type BrandLogoFieldKey = keyof CompanyConfigLogos

export const brandLogoFieldConfig: Array<{
  key: BrandLogoFieldKey
  label: string
}> = [
  { key: 'appLargeCentered', label: 'App large centered' },
  { key: 'appRegularNavigation', label: 'App regular navigation' },
  { key: 'appCompactNavigation', label: 'App compact navigation' },
  { key: 'primary', label: 'Primary (legacy fallback)' },
  { key: 'small', label: 'Small (legacy fallback)' },
]

export const getBrandLogoUrl = (slug: string): string =>
  `${brandLogoBaseUrl}/${slug}/${slug}.svg`

export const getBrandLogos = (slug: string): CompanyConfigLogos => {
  const logoUrl = getBrandLogoUrl(slug)

  return {
    small: logoUrl,
    primary: logoUrl,
    appLargeCentered: logoUrl,
    appCompactNavigation: logoUrl,
    appRegularNavigation: logoUrl,
  }
}

/** Omits logo URLs from the payload when slug is blank so stale values are not sent. */
export const stripBrandLogosWhenSlugBlank = <
  T extends Pick<Company, 'slug' | 'config'>,
>(
  values: T,
): T => {
  if (values.slug) {
    return values
  }

  const brandAssets = values.config?.brandAssets

  if (!brandAssets?.logos) {
    return values
  }

  const { logos: _logos, ...brandAssetsWithoutLogos } = brandAssets

  return {
    ...values,
    config: {
      ...(values.config ?? {}),
      brandAssets: brandAssetsWithoutLogos,
    },
  } as T
}

const isBlank = (v: unknown): boolean =>
  v === null ||
  v === '' ||
  Number.isNaN(v as number) ||
  (typeof v === 'object' &&
    !Array.isArray(v) &&
    v !== null &&
    Object.keys(v).length === 0)

export const cleanValues = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(cleanValues)
  if (typeof value === 'object' && value !== null) {
    return R.pipe(
      value as { [key: string]: unknown },
      R.mapValues(cleanValues),
      R.pickBy((v) => !isBlank(v)),
    )
  }
  return value
}
